import { Button, Flex, FormControl, FormErrorMessage } from "@chakra-ui/react";
import Input from "../inputs/Input";
import {
	type FieldValues,
	FormProvider,
	useFieldArray,
	useForm,
} from "react-hook-form";
import PlusIcon from "../icons/PlusIcon";
import type { BillingAddress } from "../../pages/SignInPage";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import _ from "lodash";

type CreateBillingAddressFormProps = {
	onSubmit?: (address: BillingAddress) => void;
	defaultValue?: BillingAddress;
};

export type CreateBillingAddressFormHandle = {
	values: () => BillingAddress;
	valid: () => Promise<boolean>;
	isDirty: () => boolean;
};

const CreateBillingAddressForm = forwardRef<
	CreateBillingAddressFormHandle,
	CreateBillingAddressFormProps
>(({ onSubmit, defaultValue }, ref) => {
	const methods = useForm();
	const { fields: additionalEmailFields, append } = useFieldArray({
		control: methods.control,
		name: "additionalEmail", // unique name
	});
	useEffect(() => {
		if (defaultValue) {
			methods.reset({
				streetName: defaultValue.streetName,
				streetNumber: defaultValue.streetNumber,
				postalCode: defaultValue.postalCode,
				city: defaultValue.city,
				country: defaultValue.country,
				email: defaultValue.email,
				siret: defaultValue.siret,
				centerCode: defaultValue.centerCode,
			});
		}
	}, [methods, defaultValue]);
	const resetFormFields = async () => {
		methods.reset({
			streetName: "",
			streetNumber: "",
			postalCode: "",
			city: "",
			country: "",
			email: "",
			additionalEmail: [],
			siret: "",
			centerCode: "",
		});
	};
	const transformFields = (fields: FieldValues) => {
		return {
			streetName: fields.streetName,
			streetNumber: fields.streetNumber,
			postalCode: fields.postalCode,
			city: fields.city,
			country: fields.country,
			email: fields.email,
			siret: fields.siret,
			centerCode: fields.centerCode,
		};
	};
	const handleSubmit = async () => {
		try {
			const valid = await methods.trigger();
			if (valid && onSubmit) {
				onSubmit(transformFields(methods.getValues()));
				resetFormFields();
			}
		} catch (err) {
			// Todo: Sentry
		}
	};

	useImperativeHandle(ref, () => ({
		values: () => {
			return methods.getValues() as BillingAddress;
		},
		valid: () => {
			return methods.trigger();
		},
		isDirty: () => {
			return !_.isEqual(methods.getValues(), {
				streetName: "",
				streetNumber: "",
				postalCode: "",
				city: "",
				country: "",
				email: "",
				additionalEmail: [],
				siret: "",
				centerCode: "",
			});
		},
	}));

	return (
		<Flex w="100%">
			<FormProvider {...methods}>
				<form
					style={{ marginTop: "32px", width: "100%" }}
					onSubmit={methods.handleSubmit(handleSubmit)}
				>
					<Flex direction="column" gap="24px">
						<Flex gap="24px">
							<FormControl
								flexShrink={2}
								isInvalid={!!methods.formState.errors.streetNumber}
							>
								<Input
									id="streetNumber"
									placeholder="Numéro"
									{...methods.register("streetNumber", {
										required: "Ce champ est obligatoire",
									})}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.streetNumber?.message}</>
								</FormErrorMessage>
							</FormControl>
							<FormControl isInvalid={!!methods.formState.errors.streetName}>
								<Input
									id="streetName"
									placeholder="Adresse de facturation"
									{...methods.register("streetName", {
										required: "Ce champ est obligatoire",
									})}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.streetName?.message}</>
								</FormErrorMessage>
							</FormControl>
						</Flex>
						<Flex gap="24px">
							<FormControl
								isInvalid={!!methods.formState.errors.postalCode}
								flexShrink={2}
							>
								<Input
									id="postalCode"
									placeholder="Code postal"
									{...methods.register("postalCode", {
										required: "Ce champ est obligatoire",
									})}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.postalCode?.message}</>
								</FormErrorMessage>
							</FormControl>
							<FormControl isInvalid={!!methods.formState.errors.city}>
								<Input
									id="city"
									placeholder="Ville"
									{...methods.register("city", {
										required: "Ce champ est obligatoire",
									})}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.city?.message}</>
								</FormErrorMessage>
							</FormControl>
						</Flex>
						<FormControl isInvalid={!!methods.formState.errors.country}>
							<Input
								id="country"
								placeholder="Pays"
								{...methods.register("country", {
									required: "Ce champ est obligatoire",
								})}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.country?.message}</>
							</FormErrorMessage>
						</FormControl>
						<FormControl isInvalid={!!methods.formState.errors.email}>
							<Input
								id="email"
								placeholder="Email de facturation"
								{...methods.register("email", {
									required: "Ce champ est obligatoire",
								})}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.email?.message}</>
							</FormErrorMessage>
						</FormControl>

						<Flex justifyContent="end">
							<Button
								variant="link"
								color="brand.700"
								onClick={() => append("")}
							>
								<PlusIcon mr="4px"></PlusIcon>Ajouter un email
							</Button>
						</Flex>

						{additionalEmailFields.map((field, index) => {
							return (
								<FormControl key={field.id}>
									<Input
										placeholder="Email de facturation"
										{...methods.register(`additionalEmail.${index}`)}
									></Input>
								</FormControl>
							);
						})}

						<FormControl isInvalid={!!methods.formState.errors.siret} mt="24px">
							<Input
								id="siret"
								placeholder="N° de SIRET"
								{...methods.register("siret", {
									required: "Ce champ est obligatoire",
								})}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.siret?.message}</>
							</FormErrorMessage>
						</FormControl>

						<FormControl isInvalid={!!methods.formState.errors.centerCode}>
							<Input
								id="centerCode"
								placeholder="Code centre (facultatif)"
								{...methods.register("centerCode")}
							></Input>
							<FormErrorMessage>
								<>{methods.formState.errors?.centerCode?.message}</>
							</FormErrorMessage>
						</FormControl>

						{/* <Flex w="100%" justifyContent={"end"}>
              <Button onClick={handleSubmit}>
                {defaultValue === null && <><PlusIcon mr="8px"></PlusIcon>Ajouter l'adresse de facturation</>}
                {defaultValue != null && <><PenIcon mr="8px"></PenIcon>Enregistrer les modifications</>}
              </Button>
            </Flex> */}
					</Flex>
				</form>
			</FormProvider>
		</Flex>
	);
});

export default CreateBillingAddressForm;
